import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { 
  ArrowLeft, 
  Activity, 
  FileText, 
  Volume2,
  Radio,
  Waves,
  ChevronLeft,
  ChevronRight,
  Euro
} from 'lucide-react';

// Sous-modules de la bulle Acoustique
const acousticModules = [
  { 
    id: 'prog-acoustic', 
    name: 'Prog Acoustic', 
    description: 'Surveillance acoustique & rapports intégrés aux mesures',
    icon: Activity,
    color: 'from-blue-600 to-cyan-600',
    borderColor: 'border-blue-500',
    bgColor: 'bg-blue-900/50',
    hoverShadow: 'rgba(59, 130, 246, 0.6)',
    route: '/acoustics/prog',
    active: true,
    features: [
      'Mesures en temps réel',
      'Alertes de dépassement',
      'Rapports automatiques',
      'Historique & tendances'
    ]
  },
  { 
    id: 'rapport-max', 
    name: 'Rapport Max', 
    description: 'Rapports avancés & récupération des données Prog Acoustic',
    icon: FileText,
    color: 'from-purple-600 to-pink-600',
    borderColor: 'border-purple-500',
    bgColor: 'bg-purple-900/50',
    hoverShadow: 'rgba(147, 51, 234, 0.6)',
    route: '/acoustics/reports',
    active: true,
    features: [
      'Rapports d\'expertise complets',
      'Import données Prog Acoustic',
      'Export PDF professionnel',
      'Modèles personnalisables'
    ]
  },
  { 
    id: 'devis', 
    name: 'Module Devis', 
    description: 'Création de pré-devis et devis détaillés pour vos missions',
    icon: Euro,
    color: 'from-amber-600 to-orange-600',
    borderColor: 'border-amber-500',
    bgColor: 'bg-amber-900/50',
    hoverShadow: 'rgba(245, 158, 11, 0.6)',
    route: '/quotes/new',
    active: true,
    features: [
      'Pré-devis rapide',
      'Devis détaillé contractuel',
      'Templates prestations',
      'Export PDF automatique'
    ]
  }
];

// Configuration du layout adaptatif
const getLayoutConfig = (moduleCount) => {
  if (moduleCount <= 2) {
    return { 
      columns: 'md:grid-cols-2', 
      cardSize: 'large',
      itemsPerPage: 2,
      iconSize: 'w-14 h-14',
      titleSize: 'text-2xl',
      showFeatures: true
    };
  } else if (moduleCount <= 4) {
    return { 
      columns: 'md:grid-cols-2 lg:grid-cols-2', 
      cardSize: 'medium',
      itemsPerPage: 4,
      iconSize: 'w-12 h-12',
      titleSize: 'text-xl',
      showFeatures: true
    };
  } else if (moduleCount <= 6) {
    return { 
      columns: 'md:grid-cols-2 lg:grid-cols-3', 
      cardSize: 'medium',
      itemsPerPage: 6,
      iconSize: 'w-10 h-10',
      titleSize: 'text-lg',
      showFeatures: true
    };
  } else if (moduleCount <= 9) {
    return { 
      columns: 'md:grid-cols-3 lg:grid-cols-3', 
      cardSize: 'small',
      itemsPerPage: 9,
      iconSize: 'w-10 h-10',
      titleSize: 'text-lg',
      showFeatures: false
    };
  } else {
    return { 
      columns: 'md:grid-cols-3 lg:grid-cols-4', 
      cardSize: 'compact',
      itemsPerPage: 8,
      iconSize: 'w-8 h-8',
      titleSize: 'text-base',
      showFeatures: false
    };
  }
};

const AcousticModuleHub = () => {
  const navigate = useNavigate();
  const [hoveredModule, setHoveredModule] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);

  const modules = acousticModules;
  const layoutConfig = getLayoutConfig(modules.length);
  const totalPages = Math.ceil(modules.length / layoutConfig.itemsPerPage);
  
  // Modules pour la page actuelle
  const startIndex = currentPage * layoutConfig.itemsPerPage;
  const visibleModules = modules.slice(startIndex, startIndex + layoutConfig.itemsPerPage);

  const nextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Styles selon la taille des cartes
  const getCardStyles = (size) => {
    switch (size) {
      case 'large':
        return { padding: 'p-8', minHeight: 'min-h-[320px]' };
      case 'medium':
        return { padding: 'p-6', minHeight: 'min-h-[280px]' };
      case 'small':
        return { padding: 'p-5', minHeight: 'min-h-[200px]' };
      case 'compact':
        return { padding: 'p-4', minHeight: 'min-h-[160px]' };
      default:
        return { padding: 'p-6', minHeight: 'min-h-[280px]' };
    }
  };

  const cardStyles = getCardStyles(layoutConfig.cardSize);

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      
      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-50 p-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-cyan-300 hover:text-cyan-100"
            data-testid="back-to-bubbles"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Retour aux bulles
          </Button>
          
          <div className="flex items-center gap-3">
            <Badge className="bg-cyan-600/20 text-cyan-300 border border-cyan-500/50">
              <Volume2 className="w-4 h-4 mr-2" />
              Module Acoustique
            </Badge>
          </div>
        </div>
      </header>

      {/* Background Animation */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 py-20">
        
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-cyan-500/30">
              <Waves className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Bulle Acoustique
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            Choisissez votre module pour accéder aux outils de mesure et d'expertise acoustique
          </p>
        </motion.div>

        {/* Modules Grid - Adaptatif */}
        <div className={`grid grid-cols-1 ${layoutConfig.columns} gap-6 max-w-5xl w-full`}>
          <AnimatePresence mode="wait">
            {visibleModules.map((module, index) => (
              <motion.div
                key={module.id}
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.95 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ 
                  scale: 1.03, 
                  boxShadow: `0 0 40px ${module.hoverShadow}` 
                }}
                onMouseEnter={() => setHoveredModule(module.id)}
                onMouseLeave={() => setHoveredModule(null)}
                onClick={() => module.active && navigate(module.route)}
                className={`cursor-pointer ${!module.active && 'opacity-50 cursor-not-allowed'}`}
                data-testid={`module-${module.id}`}
              >
                <Card className={`${module.bgColor} ${module.borderColor} border-2 backdrop-blur-sm transition-all duration-300 h-full`}>
                  <CardContent className={`${cardStyles.padding} ${cardStyles.minHeight} flex flex-col`}>
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className={`${layoutConfig.iconSize} bg-gradient-to-br ${module.color} rounded-xl flex items-center justify-center shadow-lg`}>
                        <module.icon className="w-1/2 h-1/2 text-white" />
                      </div>
                      {!module.active && (
                        <Badge variant="outline" className="border-slate-500 text-slate-400 text-xs">
                          Bientôt
                        </Badge>
                      )}
                    </div>

                    {/* Title & Description */}
                    <h3 className={`${layoutConfig.titleSize} font-bold text-white mb-2`}>
                      {module.name}
                    </h3>
                    <p className={`text-slate-300 mb-4 ${layoutConfig.cardSize === 'compact' ? 'text-sm' : ''}`}>
                      {module.description}
                    </p>

                    {/* Features - conditionnel selon la taille */}
                    {layoutConfig.showFeatures && (
                      <div className="space-y-2 mb-4 flex-grow">
                        {module.features.map((feature, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm text-slate-400">
                            <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${module.color}`} />
                            {feature}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* CTA */}
                    <Button 
                      className={`w-full bg-gradient-to-r ${module.color} hover:opacity-90 transition-opacity mt-auto ${layoutConfig.cardSize === 'compact' ? 'text-sm py-2' : ''}`}
                      disabled={!module.active}
                    >
                      {module.active ? 'Accéder au module' : 'Prochainement'}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Pagination - si plusieurs pages */}
        {totalPages > 1 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center gap-4 mt-8"
          >
            <Button
              variant="outline"
              size="sm"
              onClick={prevPage}
              disabled={currentPage === 0}
              className="border-slate-600 text-slate-300"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            
            <div className="flex gap-2">
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i)}
                  className={`w-2.5 h-2.5 rounded-full transition-colors ${
                    i === currentPage 
                      ? 'bg-cyan-400' 
                      : 'bg-slate-600 hover:bg-slate-500'
                  }`}
                />
              ))}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={nextPage}
              disabled={currentPage === totalPages - 1}
              className="border-slate-600 text-slate-300"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </motion.div>
        )}

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-8 flex flex-wrap justify-center gap-4"
        >
          <Button
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-800"
            onClick={() => navigate('/acoustics/report/new')}
            data-testid="quick-new-report"
          >
            <FileText className="w-4 h-4 mr-2" />
            Nouveau rapport rapide
          </Button>
          <Button
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-800"
            onClick={() => navigate('/chatbot')}
            data-testid="quick-assistant"
          >
            <Radio className="w-4 h-4 mr-2" />
            Assistant Acoustique
          </Button>
        </motion.div>

      </div>
    </div>
  );
};

export default AcousticModuleHub;
